import { useRuntimeConfig, navigateTo, nextTick } from '#imports';
import { runLogoutFlow as defaultRunLogoutFlow } from 'r3ui-lib';
import { authApi as defaultAuthApi } from '~/services/auth.service';
import type { AuthApi } from '~/types/ports/auth.api';

type LogoutPublicConfig = {
  authApiUrl?: string;
  origin?: string;
};

export type NavigateToFn = (
  url: string,
  opts?: { external?: boolean }
) => void | Promise<void>;

type LogoutFlowOptions = {
  logOutSSO: () => Promise<unknown>;
  nextTick: () => Promise<void>;
  navigateTo: (url: string) => void | Promise<void>;
  onReset: () => void;
  onError: (error: unknown) => void;
  redirectPath: string;
};

type LogoutDeps = {
  resetSession: () => void;
  resetStorage: () => void;
  authApi?: AuthApi;
  runLogoutFlow?: (opts: LogoutFlowOptions) => Promise<void>;
  getPublicConfig?: () => LogoutPublicConfig;
  navigateTo?: NavigateToFn;
  nextTick?: () => Promise<void>;
  onError?: (error: unknown) => void;
};

/** Сборка URL редиректа после logout (чистая функция). */
export function buildLogoutRedirectUrl({
  authApiUrl,
  origin,
}: {
  authApiUrl?: string;
  origin?: string;
}): string {
  const serviceUrl = authApiUrl?.replace(/\/$/, '');
  const baseLogoutUrl = serviceUrl ? `${serviceUrl}/logout` : '/login';
  const appOrigin = origin?.replace(/\/$/, '');
  const returnTo =
    appOrigin || (serviceUrl ? `${serviceUrl}/login` : undefined);
  return returnTo
    ? `${baseLogoutUrl}?redirect=${encodeURIComponent(returnTo)}`
    : baseLogoutUrl;
}

/** Navigate с external: true для абсолютных http(s) URL. */
export function createExternalAwareNavigate(navigateToFn: NavigateToFn) {
  return (url: string): void | Promise<void> =>
    /^https?:\/\//.test(url)
      ? navigateToFn(url, { external: true })
      : navigateToFn(url);
}

/**
 * Logout + SSO redirect use-case.
 */
export async function runAuthLogout(deps: LogoutDeps): Promise<void> {
  const api = deps.authApi ?? defaultAuthApi;
  const runFlow = deps.runLogoutFlow ?? defaultRunLogoutFlow;
  const getPublicConfig =
    deps.getPublicConfig ??
    (() => {
      const config = useRuntimeConfig();
      return {
        authApiUrl: config.public.authApiUrl as string | undefined,
        origin: config.public.origin as string | undefined,
      };
    });
  const navigate = (deps.navigateTo ?? navigateTo) as NavigateToFn;
  const tick = deps.nextTick ?? nextTick;

  const { authApiUrl, origin } = getPublicConfig();
  const redirectPath = buildLogoutRedirectUrl({ authApiUrl, origin });

  await runFlow({
    logOutSSO: () => api.logOutSSO(),
    nextTick: tick,
    navigateTo: createExternalAwareNavigate(navigate),
    onReset: () => {
      deps.resetSession();
      deps.resetStorage();
    },
    ...(deps.onError
      ? { onError: deps.onError }
      : {
          onError: (error: unknown) => {
            // eslint-disable-next-line no-console
            console.error('[logout]', error);
          },
        }),
    redirectPath,
  });
}
